/**
 * dBm-to-color mapper for the CoverageMap replay overlay (Phase 10 Plan 10-02 T2/T7).
 *
 * Lives under `coverage-map/` because the helper is consumed by the
 * CoverageMap arc rendering as well as by the alert-replay feature; it's
 * a generic "smoothed-RSSI to theme-token color" helper that does not
 * depend on the alert-replay feature surface.
 *
 * Color tokens map to the existing SOC palette via vanilla-extract var
 * references so colors swap automatically in light vs dark mode:
 *   - dBm > -60         => status.alert (red)    [close band]
 *   - dBm in (-75..-60] => status.warn  (gold)   [mid band]
 *   - dBm <= -75        => status.ok    (green)  [far band]
 *   - null / undefined  => text.tertiary (muted neutral)
 *
 * Bands are inclusive on the "far" side and exclusive on the "close"
 * side; -75 dBm reads as far/green; -74 reads as mid/yellow; -60 reads
 * as mid/yellow; -59 reads as close/red.
 */
import { theme } from '@/design/tokens.css';

/** Default fill color when no RSSI data is available for a zone. */
export const NO_DATA_COLOR = theme.colors.text.tertiary;

/**
 * Map a smoothed dBm value to one of three theme color tokens.
 *
 * @param dbm Smoothed dBm value, or null/undefined when no data.
 * @returns CSS color string (vanilla-extract var() reference).
 */
export function dbmToColor(dbm: number | null | undefined): string {
  if (dbm === null || dbm === undefined || !Number.isFinite(dbm)) {
    return NO_DATA_COLOR;
  }
  if (dbm > -60) {
    return theme.colors.status.alert;
  }
  if (dbm > -75) {
    return theme.colors.status.warn;
  }
  return theme.colors.status.ok;
}
